import type { Project, Task, TaskStatus } from "@/api/queries";

// Categorical project colors, validated for color-vision deficiencies (docs/08-web-ui.md).
export const defaultColor = "#2a78d6";
export const palette = [
  defaultColor,
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
];

export const taskStatuses: TaskStatus[] = ["todo", "in_progress", "done"];

export const statusBadge = {
  todo: "neutral",
  in_progress: "primary",
  done: "success",
} as const satisfies Record<TaskStatus, "neutral" | "primary" | "success">;

export type BudgetLevel = "ok" | "warning" | "over";

/** Share of the budget spent; thresholds follow F-PRJ-5 (80% and 100%). */
export function budgetUsage(project: Pick<Project, "tracked_seconds" | "budget_minutes">) {
  if (!project.budget_minutes) return null;
  const percent = Math.floor((project.tracked_seconds / 60 / project.budget_minutes) * 100);
  const level: BudgetLevel = percent >= 100 ? "over" : percent >= 80 ? "warning" : "ok";
  return { percent, level };
}

/** "1,5" or "1.5" hours → minutes; empty → undefined; NaN for invalid input. */
export function hoursToMinutes(value: string): number | undefined {
  const text = value.trim().replace(",", ".");
  if (!text) return undefined;
  const hours = Number(text);
  if (!Number.isFinite(hours) || hours <= 0) return Number.NaN;
  return Math.round(hours * 60);
}

/** Minutes → hours for an input field: 90 → "1.5". */
export function minutesToHours(minutes: number | undefined): string {
  if (!minutes) return "";
  return String(Math.round((minutes / 60) * 100) / 100);
}

/** A task is overdue when it is not done and its due date is before today (YYYY-MM-DD). */
export function isOverdue(task: Pick<Task, "due_date" | "status">, today: string): boolean {
  return Boolean(task.due_date && task.status !== "done" && task.due_date < today);
}

/** "1 окт." for a YYYY-MM-DD date; the year is added when it differs from this year's. */
export function formatDueDate(date: string, locale: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: d.getUTCFullYear() === new Date().getFullYear() ? undefined : "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** Sorts tasks: open before done, then by due date (none last), then by creation. */
export function compareTasks(a: Task, b: Task): number {
  const done = Number(a.status === "done") - Number(b.status === "done");
  if (done) return done;
  if (a.due_date !== b.due_date) {
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date < b.due_date ? -1 : 1;
  }
  return a.created_at.localeCompare(b.created_at);
}
